
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Button } from "./ui/button";
import { useLanguage } from "@/contexts/LanguageContext";

interface ProductCardProps {
  title: string;
  subtitle?: string;
  description: string;
  image?: string;
  images?: string[];
  link: string;
  className?: string;
  isSoftware?: boolean;
  showVPXLogos?: boolean;
}

export const ProductCard = ({
  title,
  subtitle,
  description,
  image,
  images,
  link,
  className = "",
  isSoftware = false,
  showVPXLogos = false,
}: ProductCardProps) => {
  const { t } = useLanguage();
  
  // Format product name to handle newlines
  const formattedTitle = t(title).split('\n').map((line, index) => (
    <span key={index} className={index > 0 ? "block text-sm font-medium mt-1" : ""}>
      {line}
    </span>
  ));
  
  // Choose the most appropriate image to display (first item in images array or fallback to image prop)
  const displayImage = images && images.length > 0 ? images[0] : (image || "/placeholder.svg");
  
  // Special handling for RXT-4D to link to features page
  const getActualLink = () => {
    if (link === "/product/rxt-4-d") {
      return "/features-benefits-rxt4d";
    }
    return link;
  };

  const threeLogos = [
    "/lovable-uploads/06fe1782-824d-4c1d-9254-aab5065810bf.png", // Dante
    "/lovable-uploads/dc50d6e6-ebe6-4761-a9a7-4221a9044168.png", // AURACAST
    "/lovable-uploads/67aa6f30-ffde-42a8-b227-d61c7af63ddc.png"  // AES
  ];
  
  return (
    <div className={cn("h-[400px] w-full", className)}>
      <div className="h-full w-full bg-white rounded-md shadow hover:shadow-md transition-shadow">
        <Link 
          to={getActualLink()}
          className="flex flex-col h-full p-6"
        >
          <div className="text-center mb-4">
            <h3 className="text-xl font-semibold tracking-tight text-foreground">
              {formattedTitle}
            </h3>
            {subtitle && (
              <p className="text-sm text-muted-foreground mt-1">
                {t(subtitle)}
              </p>
            )}
          </div>
          
          <div className="flex-grow flex flex-col items-center justify-center">
            <div className="relative aspect-square w-full max-w-[240px] overflow-hidden rounded-lg bg-background/50 mb-4">
              <img
                src={displayImage}
                alt={t(title)}
                className="h-full w-full object-scale-down p-4"
                loading="lazy"
                onError={(e) => {
                  console.error(`Failed to load image: ${displayImage}`);
                  e.currentTarget.src = "/placeholder.svg";
                }}
              />
            </div>
            
            {/* Aurora Logo */}
            <div className="w-24 h-auto flex items-center justify-center mb-3">
              <img
                src="/lovable-uploads/29f5a874-3103-490e-ae29-e107bf621df9.png"
                alt="Aurora Logo"
                className="w-full h-auto object-contain"
              />
            </div>

            {/* Two logos row for Meeting Room products - exact spacing from AVOIPOptions without middle logo */}
            {showVPXLogos && (
              <div className="flex items-center justify-between gap-6 w-full max-w-[200px]">
                {/* Dante logo - larger and positioned left */}
                <div className="w-14 h-10 flex items-center justify-start">
                  <img
                    src={threeLogos[0]}
                    alt="Dante Logo"
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
                {/* AES logo - smaller and positioned right */}
                <div className="w-10 h-8 flex items-center justify-end">
                  <img
                    src={threeLogos[2]}
                    alt="AES Logo"
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
              </div>
            )}
          </div>
        </Link>
      </div>
    </div>
  );
};
